import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

function useIntersection(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const VALUE_LETTERS = ["I", "K", "O", "B"] as const;
const VALUE_WORD_KEYS = ["about.val_i_word", "about.val_k_word", "about.val_o_word", "about.val_b_word"] as const;
const VALUE_DESC_KEYS = ["about.val_i_desc", "about.val_k_desc", "about.val_o_desc", "about.val_b_desc"] as const;

export default function AboutSection() {
  const { t } = useTranslation();
  const { ref, visible } = useIntersection();
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [activeLetter, setActiveLetter] = useState<string>("O");

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.2 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { numKey: "about.stat1_num", labelKey: "about.stat1_label" },
    { numKey: "about.stat2_num", labelKey: "about.stat2_label" },
    { numKey: "about.stat3_num", labelKey: "about.stat3_label" },
    { numKey: "about.stat4_num", labelKey: "about.stat4_label" },
  ];

  return (
    <>
      <section
        id="about"
        className="py-24 md:py-32 px-4"
        style={{ backgroundColor: "oklch(0.13 0.001 10)" }}
      >
        <div className="max-w-7xl mx-auto">
          <div
            ref={ref}
            className={`mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <p
              className="text-xs font-label tracking-widest uppercase mb-4"
              style={{ color: "oklch(0.74 0.18 55)" }}
            >
              {t("about.label")}
            </p>
            <h2
              className="font-heading font-black text-3xl md:text-5xl leading-tight mb-8"
              style={{ color: "white" }}
            >
              {t("about.title1")}<br />{t("about.title2")}
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
              {/* Text content */}
              <div
                className={`transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"}`}
              >
                <p className="font-label text-sm md:text-base leading-relaxed mb-4" style={{ color: "rgba(255,255,255,0.55)" }}>
                  {t("about.p1_pre")}
                  <strong style={{ color: "rgba(255,255,255,0.85)" }}>{t("about.p1_bold")}</strong>
                  {t("about.p1_suf")}
                </p>
                <p className="font-label text-sm md:text-base leading-relaxed mb-4" style={{ color: "rgba(255,255,255,0.55)" }}>
                  {t("about.p2_pre")}
                  <strong style={{ color: "oklch(0.74 0.18 55)" }}>MOMO</strong>
                  {t("about.p2_suf")}
                </p>
                <p className="font-label text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.40)" }}>
                  {t("about.p3")}
                </p>
              </div>

              {/* I·K·O·B Value Cards */}
              <div
                className={`grid grid-cols-2 gap-3 transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"}`}
              >
                {VALUE_LETTERS.map((letter, i) => {
                  const isActive = activeLetter === letter;

                  return (
                    <div
                      key={letter}
                      onMouseEnter={() => setActiveLetter(letter)}
                      className={`rounded-xl p-4 border transition-all duration-300 cursor-pointer
                        ${visible ? "opacity-100 scale-100" : "opacity-0 scale-95"}
                        ${isActive ? "scale-[1.02] shadow-lg" : ""}`}
                      style={{
                        transitionDelay: `${i * 80 + 200}ms`,
                        backgroundColor: isActive ? "rgba(245,158,11,0.12)" : "rgba(255,255,255,0.04)",
                        borderColor: isActive ? "rgba(245,158,11,0.45)" : "rgba(255,255,255,0.07)",
                        boxShadow: isActive ? "0 8px 32px rgba(245,158,11,0.15)" : undefined,
                      }}
                    >
                      <div className="flex items-baseline gap-2 mb-2">
                        <span
                          className="font-heading font-black text-3xl leading-none transition-colors duration-300"
                          style={{ color: isActive ? "oklch(0.74 0.18 55)" : "white" }}
                        >
                          {letter}
                        </span>
                        <span
                          className="font-label font-semibold text-sm transition-colors duration-300"
                          style={{ color: isActive ? "oklch(0.74 0.18 55)" : "rgba(255,255,255,0.7)" }}
                        >
                          {t(VALUE_WORD_KEYS[i])}
                        </span>
                      </div>
                      <p
                        className="text-xs font-label leading-relaxed transition-colors duration-300"
                        style={{ color: isActive ? "rgba(255,255,255,0.65)" : "rgba(255,255,255,0.45)" }}
                      >
                        {t(VALUE_DESC_KEYS[i])}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <div
        ref={statsRef}
        style={{ backgroundColor: "oklch(0.16 0.005 55)" }}
        className="py-8 px-4"
      >
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-px">
          {stats.map((s, i) => (
            <div
              key={s.numKey}
              className={`text-center py-6 px-4 transition-all duration-700 ${
                statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div
                className="font-heading font-black text-3xl md:text-4xl mb-2 leading-none"
                style={{ color: "oklch(0.74 0.18 55)" }}
              >
                {t(s.numKey)}
              </div>
              <div
                className="text-xs font-label leading-relaxed whitespace-pre-line"
                style={{ color: "rgba(255,255,255,0.5)" }}
              >
                {t(s.labelKey)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}